import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, Chip, FormControl, FormLabel, Modal, ModalClose, ModalDialog, Option, Select, Stack, Typography } from '@mui/joy';
import { motion, useReducedMotion } from 'framer-motion';
import { AppContext } from '../../context';
import { gcoinDate, gcoinRequest } from '../../gcoinApi';
import useGCoinMutation from '../../hooks/useGCoinMutation';
import { cardSx, dialogSx } from './GCoinUI';

const modeLabel = mode => mode === 'both' ? 'Начислять за обе обязанности' : 'Начислять только фотографу';

export default function GCoinProgramPanel({ refreshKey, onActivated }) {
    const { token, account, showToast } = useContext(AppContext);
    const activation = useGCoinMutation(token, account.id, 'activation');
    const settings = useGCoinMutation(token, account.id, 'program-settings');
    const reduced = useReducedMotion();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState(null);
    const [mode, setMode] = useState(null);
    const [expectedId, setExpectedId] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const controller = useRef(null);
    const requestId = useRef(0);
    const load = useCallback(async () => {
        controller.current?.abort();
        const abort = new AbortController(); controller.current = abort;
        const id = ++requestId.current;
        setLoading(true); setError('');
        try {
            const data = await gcoinRequest(token, 'program/', { signal: abort.signal });
            if (id === requestId.current) setStatus(data);
        } catch (err) { if (!abort.signal.aborted && id === requestId.current) setError(err.message); }
        finally { if (id === requestId.current) setLoading(false); }
    }, [token]);
    useEffect(() => {
        load();
        const timer = setInterval(() => { if (!document.hidden) load(); }, 30000);
        return () => { clearInterval(timer); ++requestId.current; controller.current?.abort(); };
    }, [load, refreshKey]);
    const pending = activation.pending || settings.pending;
    const editing = pending ? pending.path === 'program/settings/' : form === 'settings';
    const mutation = editing ? settings : activation;
    const locked = activation.busy || settings.busy || !!pending;
    const startEdit = () => {
        const kind = status.enabled ? 'settings' : 'activation';
        (kind === 'settings' ? settings : activation).clearError();
        setForm(kind); setMode(status.dual_role_mode || 'both');
        setExpectedId(status.settings_id); setConfirmed(false);
    };
    const submit = async () => {
        const path = editing ? 'program/settings/' : 'program/';
        const payload = editing ? { dual_role_mode: mode, expected_policy_id: expectedId } : { dual_role_mode: mode };
        const result = await mutation.run(path, payload, editing ? 'Настройки начислений' : 'Запуск автоматических начислений');
        if (result) {
            setForm(null);
            showToast(editing ? 'Настройки сохранены для новых продаж' : 'Автоматические начисления запущены');
            load(); onActivated?.();
        }
    };
    return <Box sx={cardSx}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
            <Typography sx={{ fontWeight: 800 }}>Начисления от продаж</Typography>
            <Button variant="plain" color="neutral" loading={loading} onClick={load}>Обновить статус</Button>
        </Stack>
        {error && <Alert color="danger" sx={{ mt: 1 }}>{error}</Alert>}
        {status && <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Chip size="sm" color={status.enabled ? 'success' : 'neutral'}>{status.enabled ? 'Программа запущена' : 'Ожидает запуска'}</Chip>
                <Chip size="sm" color={status.worker_online ? 'success' : 'warning'}>{status.worker_online ? 'Обработчик на связи' : 'Нет свежего ответа обработчика'}</Chip>
            </Stack>
            {status.enabled ? <>
                <Typography level="body-sm">Старт: {gcoinDate(status.started_at)} МСК. Новые продажи начисляют монеты по действовавшему на момент продажи курсу.</Typography>
                <Typography level="body-sm">Один сотрудник в обеих ролях: {modeLabel(status.dual_role_mode).toLowerCase()}.</Typography>
                <Typography level="body-sm">В очереди: {status.pending} · С ошибкой: {status.failed}</Typography>
                {!!status.failed && <Alert color="warning">Часть начислений ожидает повторной обработки. История успешных операций сохранена.</Alert>}
                <Button variant="outlined" onClick={startEdit} disabled={locked || !status.settings_id}>Настроить начисления</Button>
                {!status.settings_id && <Alert color="warning">Не удалось получить настройки. Обнови статус.</Alert>}
                <Typography level="body-xs">Правило можно менять для новых продаж. Дата запуска и начисления старых чеков сохраняются.</Typography>
                {!!status.settings_history?.length && <Box component="details">
                    <Typography component="summary" sx={{ cursor: 'pointer', fontSize: 13 }}>История настроек начислений</Typography>
                    {status.settings_history.map(row => <Box key={row.id} sx={{ pt: 1 }}>
                        <Typography level="body-sm">{modeLabel(row.dual_role_mode)}</Typography>
                        <Typography level="body-xs">С {gcoinDate(row.effective_from)} МСК · {row.actor_name || 'Начальные настройки'}</Typography>
                    </Box>)}
                </Box>}
            </> : <>
                <Typography level="body-sm">Запуск включает продажи с текущего момента. Старые чеки не начисляются задним числом.</Typography>
                {!status.worker_online && <Alert color="warning">Сначала подключи обработчик на сервере и дождись статуса «на связи».</Alert>}
                <Button onClick={startEdit} disabled={locked || !status.worker_online}>Настроить и запустить</Button>
            </>}
        </Stack>}
        <Modal open={!!form || !!pending} onClose={() => { if (!locked) setForm(null); }}>
            <ModalDialog sx={dialogSx} aria-labelledby="gcoin-activation-title">
                <ModalClose aria-label="Закрыть" disabled={locked} />
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: reduced ? 0 : 0.2 }}>
                    <Stack spacing={2}>
                        <Typography id="gcoin-activation-title" sx={{ fontWeight: 800, fontSize: 18 }}>{editing ? 'Настройки начислений от продаж' : 'Запустить G coin от продаж'}</Typography>
                        <Typography level="body-sm">{editing ? 'Изменение действует для продаж с момента сохранения. Старые чеки, их исправления и отмены используют прежнее правило.' : 'Дата старта фиксируется один раз. Курсы берутся из настроек выше.'}</Typography>
                        {pending ? <Alert color="warning">Результат запроса ещё не подтверждён. Повтор отправит ту же операцию. Правило: {modeLabel(pending.payload.dual_role_mode)}.</Alert> : <>
                            <FormControl required><FormLabel>Если в чеке фотограф и администратор — один человек</FormLabel>
                                <Select value={mode} onChange={(_, value) => setMode(value)}>
                                    <Option value="both">Начислять за обе обязанности</Option>
                                    <Option value="photographer_only">Начислять только фотографу</Option>
                                </Select>
                            </FormControl>
                            <Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
                                label={editing ? 'Применяем к новым продажам, без пересчёта старых' : 'Запускаем с текущего момента, без старых чеков'} />
                        </>}
                        {mutation.error && <Alert color="danger">{mutation.error}</Alert>}
                        <Button loading={mutation.busy} disabled={!pending && (!mode || !confirmed || (editing && !expectedId))} onClick={submit}>
                            {pending ? 'Повторить тот же запрос' : editing ? 'Сохранить настройки' : 'Запустить начисления'}
                        </Button>
                    </Stack>
                </motion.div>
            </ModalDialog>
        </Modal>
    </Box>;
}